import { HistoricalWorkItem, WorkItemResult } from "@buzzeasy/shared-frontend-utilities";
import { theme, Typography } from "antd";
import { Popover, Tag } from "antd-mobile";
import { ListItem } from "antd-mobile/es/components/list/list-item";
import dayjs from "dayjs";
import { SquareArrowDownRight, SquareArrowUpLeft } from "lucide-react";
import { FC, useCallback, useEffect, useRef } from "react";
import { styled } from "styled-components";
import { usePress } from "../../hooks/usePress";
import { getResultTagBackgroundColor, getResultTagColor, getWorkItemResultIcon } from "./TaskListItem.utils";

interface IProps {
  workItem: HistoricalWorkItem;
  isOutbound: boolean;
  phoneNumber: string;
  isFilteredView: boolean;
  isPopupOpen: boolean;
  onPopupOpenChange(isOpen: boolean): void;
}

const { Text } = Typography;

const TaskListItem: FC<IProps> = ({ workItem, isOutbound, phoneNumber, isFilteredView, isPopupOpen, onPopupOpenChange }) => {
  const { token } = theme.useToken();

  const { isPressed, ...pressHandlers } = usePress({ onLongPress: () => onPopupOpenChange(true) });

  const phoneNumberContainerRef = useRef<HTMLDivElement>(null);

  const onCopyPhoneNumber = useCallback(
    async () => {
      try {
        await navigator.clipboard.writeText(phoneNumber);
      }
      catch {
        /* do nothing */
      }
      onPopupOpenChange(false);
    },
    [phoneNumber, onPopupOpenChange],
  );

  useEffect(
    () => {
      if (!isPopupOpen) {
        return;
      }

      const handleOutsideTap = (event: MouseEvent) => {
        const target = event.target as Element;
        if (phoneNumberContainerRef.current?.contains(target) || target.closest?.(".adm-popover")) {
          return;
        }
        onPopupOpenChange(false);
      };

      const handleScroll = () => onPopupOpenChange(false);

      document.addEventListener("click", handleOutsideTap);
      document.addEventListener("scroll", handleScroll, { capture: true, passive: true });

      return () => {
        document.removeEventListener("click", handleOutsideTap);
        document.removeEventListener("scroll", handleScroll, { capture: true });
      };
    },
    [isPopupOpen, onPopupOpenChange],
  );

  const DirectionIcon = isOutbound ? SquareArrowUpLeft : SquareArrowDownRight;

  return (
    <StyledListItem
      description={
        <Grid $isFilteredView={isFilteredView}>
          <Row>
            {
              !isFilteredView &&
              <GridCell>
                <StyledTag
                  round
                  fill="outline"
                  color={getResultTagColor(workItem.result)}
                  $result={workItem.result}
                >
                  <HorizontalFlexBox gap={token.sizeXXS}>
                    {getWorkItemResultIcon(workItem.result, token.sizeSM)} {workItem.result}
                  </HorizontalFlexBox>
                </StyledTag>
              </GridCell>
            }
            <GridCell>
              <HorizontalFlexBox gap={token.sizeXXS}>
                <DirectionIcon size={token.size} strokeWidth={1.5} color={token.colorText} />
                <Text style={{ fontSize: token.fontSizeSM }}>
                  {isOutbound ? "out" : "in"}
                </Text>
              </HorizontalFlexBox>
            </GridCell>
            <GridCell>
              <Text style={{ fontSize: token.fontSizeSM }}>
                {dayjs(workItem.startTimeUtc).format("YYYY.MM.DD. HH:mm")}
              </Text>
            </GridCell>
          </Row>
        </Grid>
      }
      // for the next PBI
      // extra={
      //   <CallButton shape="rounded" size="small" color="primary" type="submit">
      //     <Phone size={token.size} style={{ display: "block" }} />
      //   </CallButton>
      // }
    >
      <StyledPopover
        placement="bottom-end"
        visible={isPopupOpen}
        onVisibleChange={onPopupOpenChange}
        content={<div onClick={onCopyPhoneNumber}>Copy</div>}
      >
        <PhoneNumberContainer
          ref={phoneNumberContainerRef}
          $isHighlighted={isPressed || isPopupOpen}
          {...pressHandlers}
        >
          <Text strong style={{ color: workItem.result === "Handled" ? token.colorText : token.colorErrorText, fontSize: token.fontSizeLG }}>
            {phoneNumber}
          </Text>
        </PhoneNumberContainer>
      </StyledPopover>
    </StyledListItem>
  );
};

export default TaskListItem;

const StyledListItem = styled(ListItem)`
  background-color: ${p => p.theme.colorFillAlter};
  border: none;
  border-radius: ${p => p.theme.borderRadius}px;
  margin-bottom: ${p => p.theme.marginXS}px;
  box-shadow: 0 6px 10px -8px rgba(16, 24, 40, 0.45), 0 2px 4px -3px rgba(16, 24, 40, 0.3);
`;

export const HorizontalFlexBox = styled.div<{ gap: number }>`
  display: flex;
  align-items: center;
  gap: ${p => p.gap}px;
`;

const Grid = styled.div<{ $isFilteredView: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$isFilteredView
      ? "minmax(3.75rem, 1fr) minmax(0, 3fr)"
      : "minmax(6.25rem, 2fr) minmax(3.75rem, 1fr) minmax(0, 3fr)"
  };
  padding-top: ${p => p.theme.paddingXS}px;
`;

const Row = styled.div`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
  column-gap: ${p => p.theme.sizeXS}px;
`;

const GridCell = styled.div`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const PhoneNumberContainer = styled.div<{ $isHighlighted: boolean }>`
  width: fit-content;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  background-color: ${p => p.$isHighlighted ? `${p.theme.colorPrimary}33` : "transparent"};
`;

const StyledTag = styled(Tag)<{ $result: WorkItemResult }>`
  background-color: ${p => getResultTagBackgroundColor(p.$result)};
  border-radius: ${p => p.theme.borderRadiusLG}px;
  padding: ${p => p.theme.paddingXXS}px ${p => p.theme.paddingXS}px;
`;

const StyledPopover = styled(Popover)`
  --arrow-size: 0px;
  margin-top: -${p => p.theme.marginSM}px;

  .adm-popover-inner-content {
    background-color: ${p => `${p.theme.colorPrimary}66`};
    padding: ${p => p.theme.paddingSM}px ${p => p.theme.padding}px; 
  }
`;

// const CallButton = styled(Button)`
//   width: 2.4rem;
//   height: 2.4rem;
//   padding: 0;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
// `;
